import React, {useState} from 'react';

import {assert} from 'Asserts';
import Modal from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant, StepNavSize, StepItemStatus} from 'ui-library/StepNav';

import {AntibodyCreationStepKey, ANTIBODY_CREATION_STEPS} from './constants/AntibodyCreationConstants';
import FinalizePane from './panes/FinalizePane';
import GetConstantDomainsPane from './panes/GetConstantDomainsPane';
import GetVariableDomainsPane from './panes/GetVariableDomainsPane';
import SelectFormatPane from './panes/SelectFormatPane';

interface CreateAntibodiesFromPairsModalProps {
  onRequestClose: () => void;
}

export default function CreateAntibodiesFromPairsModal(
  props: CreateAntibodiesFromPairsModalProps
): JSX.Element {
  // TODO(BNCH-131837): track state (including data needed to create antibodies) in a separate reducer.
  const [currentStep, setCurrentStep] = useState<AntibodyCreationStepKey>(
    AntibodyCreationStepKey.SELECT_FORMAT
  );

  const handleNext = (): void => {
    const currentStepIndex = ANTIBODY_CREATION_STEPS.findIndex((step) => step.key === currentStep);
    if (currentStepIndex === ANTIBODY_CREATION_STEPS.length - 1) {
      // TODO(BNCH-131662): use onRequestSubmit for last step
      props.onRequestClose();
      return;
    }

    const nextStep = ANTIBODY_CREATION_STEPS[currentStepIndex + 1];
    setCurrentStep(nextStep.key);
  };

  const handlePrevious = (): void => {
    const currentStepIndex = ANTIBODY_CREATION_STEPS.findIndex((step) => step.key === currentStep);
    if (currentStepIndex === 0) {
      return;
    }

    const prevStep = ANTIBODY_CREATION_STEPS[currentStepIndex - 1];
    setCurrentStep(prevStep.key);
  };

  const renderStepContent = (): JSX.Element => {
    switch (currentStep) {
      case AntibodyCreationStepKey.SELECT_FORMAT:
        return <SelectFormatPane />;

      case AntibodyCreationStepKey.GET_VARIABLE_DOMAINS:
        return <GetVariableDomainsPane />;

      case AntibodyCreationStepKey.GET_CONSTANT_DOMAINS:
        return <GetConstantDomainsPane />;

      case AntibodyCreationStepKey.FINALIZE:
        return <FinalizePane />;

      default:
        assert(false, `Unexpected antibody creation step: ${currentStep}`);
    }
  };

  const currentStepIndex = ANTIBODY_CREATION_STEPS.findIndex((step) => step.key === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === ANTIBODY_CREATION_STEPS.length - 1;

  // Simple status tracking - mark steps as visited based on progression
  const statusByStep: {[key in AntibodyCreationStepKey]: StepItemStatus | null} = {
    [AntibodyCreationStepKey.SELECT_FORMAT]:
      currentStepIndex > 0 ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
    [AntibodyCreationStepKey.GET_VARIABLE_DOMAINS]:
      currentStepIndex > 1 ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
    [AntibodyCreationStepKey.GET_CONSTANT_DOMAINS]:
      currentStepIndex > 2 ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
    [AntibodyCreationStepKey.FINALIZE]: StepItemStatus.UNVISITED,
  };

  const buttons = [
    {
      text: 'Cancel',
      onClick: props.onRequestClose,
      cancel: true,
    },
    ...(!isFirstStep
      ? [
          {
            text: 'Previous',
            onClick: handlePrevious,
          },
        ]
      : []),
    {
      text: isLastStep ? 'Create antibodies' : 'Next',
      onClick: handleNext,
      submit: isLastStep,
    },
  ];

  return (
    <Modal
      title="Create antibodies"
      buttons={buttons}
      onRequestClose={props.onRequestClose}
      scrollable={true}
      expandHeight={true}
      large={true}
    >
      <StepNav
        steps={ANTIBODY_CREATION_STEPS}
        statusByStep={statusByStep}
        currentStepKey={currentStep}
        onUpdateCurrentStep={(step) => {
          setCurrentStep(step);
        }}
        variant={StepNavVariant.STACKED}
        size={StepNavSize.LARGE}
      />
      <div data-testid="step-content">{renderStepContent()}</div>
    </Modal>
  );
}
